/* eslint-disable react/no-array-index-key */
import { Avatar } from '@lmc/ui-kit'
import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import EventTitlePopover from '../popovers/EventTitlePopover'
import EventIcon from '../EventIcon'
import AllocationPopover from '../popovers/AllocationPopover'
import { useTranslation } from 'react-i18next'
import { useState, useEffect, useRef } from 'react'
import AllocationWindow from '../../../screens/AllocationWindow/AllocationWindow'
import {
  getDateTimeInZone,
  TIME_FORMAT
} from 'screens/Rostering/utils.datetime'
import { Home, User } from '@lmc/ts-common'
import { RESIDENT, SHIFT } from 'screens/Rostering/constants'
import useFeatureFlags from 'hooks/useFeatureFlags'
import WarningPopover from '../popovers/WarningPopover'
import { WarningIcon } from '../icons'
import dayjs from 'dayjs'
import AllocateIcon from '../icons/AllocateIcon'
import { convertToMinutes } from './utils'
import { RotaInstanceStatus } from 'screens/Rostering/types'
import { useSelector } from 'react-redux'
import { getNominative } from '@lmc/common/utils'
import QuickAllocationPopover from '../popovers/QuickAllocationPopover'
import { commonPatterns } from 'utils/enhancedTestIds'

type EventProps = {
  args: {
    original: {
      id: string
      teamMembers: { carer: User; teamMemberId: string }[]
      expectedDuration: string
      startTime: string
      endTime: string
      status: RotaInstanceStatus
      modified: boolean
      title: string
      flexiType: boolean
      instanceType: string
      numberSupply: number
      resident: User
      deletedServiceUser: string | undefined
    }
    start: string
    end: string
    color: string
  }
  type?: 'user' | 'shift' | 'resident'
  warnings: string[]
  setDrawerVisible: (flag: boolean) => void
  setDrawerAllocatedTeamVisible: (flag: boolean) => void
  isDailyView: boolean
  publishedRota?: boolean
  testId?: string
}

const Event = ({
  args,
  type = RESIDENT,
  warnings,
  setDrawerVisible,
  setDrawerAllocatedTeamVisible,
  isDailyView,
  publishedRota,
  testId
}: EventProps) => {
  const { t } = useTranslation()
  const baseId = testId || 'rota-event'

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )

  const {
    original: {
      teamMembers,
      expectedDuration,
      startTime,
      endTime,
      status,
      modified,
      title,
      flexiType,
      // instanceType,
      numberSupply,
      resident,
      deletedServiceUser
    },
    start,
    end,
    color
  } = args

  const flags = useFeatureFlags({
    flagNames: ['rostering_parked_features', 'rostering_quick_allocation']
  })?.data

  const flagsRosteringParkedFeaturesEnabled =
    flags?.rostering_parked_features?.enabled

  const flagsRosteringQuickAllocationEnabled =
    flags?.rostering_quick_allocation?.enabled

  if (!args?.original) return null

  const [allocationWindowVisible, setAllocationWindowVisible] = useState(false)

  const [allocationPopoverOpen, setAllocationPopoverOpen] = useState(false)
  const [quickAllocationPopoverOpen, setQuickAllocationPopoverOpen] =
    useState(false)
  const [titlePopoverOpen, setTitlePopoverOpen] = useState(false)
  const [warningPopoverOpen, setWarningPopoverOpen] = useState(false)

  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isPointerOverCardRef = useRef(false)
  const innerPopoversOpenRef = useRef({
    allocation: false,
    quick: false,
    warning: false
  })

  useEffect(() => {
    innerPopoversOpenRef.current = {
      allocation: allocationPopoverOpen,
      quick: quickAllocationPopoverOpen,
      warning: warningPopoverOpen
    }
  }, [allocationPopoverOpen, quickAllocationPopoverOpen, warningPopoverOpen])

  const formatTime = (time: string) => {
    if (time.includes('/')) {
      return dayjs(time).format('HH:mm')
    }
    return dayjs(time, TIME_FORMAT).format('HH:mm')
  }

  const timeInterval = `${formatTime(start)}-${formatTime(end)}`

  const isOvernight =
    getDateTimeInZone(startTime).date() < getDateTimeInZone(endTime).date()

  const clearTitleHoverSchedule = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
  }

  const closeAllPopovers = () => {
    isPointerOverCardRef.current = false
    clearTitleHoverSchedule()
    setTitlePopoverOpen(false)
    setAllocationPopoverOpen(false)
    setQuickAllocationPopoverOpen(false)
    setWarningPopoverOpen(false)
  }

  const handleOpenDrawer = () => {
    closeAllPopovers()
    setDrawerVisible(true)
  }

  const handleIconClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()

    if (
      deletedServiceUser ||
      status === RotaInstanceStatus.CANCELLED ||
      status === RotaInstanceStatus.DONE
    ) {
      closeAllPopovers()
      setDrawerAllocatedTeamVisible(true)
    } else if (flagsRosteringQuickAllocationEnabled) {
      setTitlePopoverOpen(false)
      setWarningPopoverOpen(false)
      clearTitleHoverSchedule()
      setQuickAllocationPopoverOpen(true)
      setAllocationPopoverOpen(false)
    } else {
      closeAllPopovers()
      setAllocationWindowVisible(true)
    }
  }

  const handleEventMouseEnter = () => {
    isPointerOverCardRef.current = true
    clearTitleHoverSchedule()

    hoverTimeoutRef.current = setTimeout(() => {
      hoverTimeoutRef.current = null
      if (!isPointerOverCardRef.current) {
        return
      }
      const { allocation, quick, warning } = innerPopoversOpenRef.current
      if (!allocation && !quick && !warning) {
        setTitlePopoverOpen(true)
      }
    }, 1000)
  }

  const handleEventMouseLeave = () => {
    isPointerOverCardRef.current = false
    clearTitleHoverSchedule()
    setTitlePopoverOpen(false)
  }

  // When inner popovers close, only reopen the title popover if the pointer is still over the card
  const handleInnerPopoverClose = () => {
    setTimeout(() => {
      if (!isPointerOverCardRef.current) {
        return
      }
      const { allocation, quick, warning } = innerPopoversOpenRef.current
      if (!allocation && !quick && !warning) {
        setTitlePopoverOpen(true)
      }
    }, 50)
  }

  // Close event popovers when the allocation window opens
  useEffect(() => {
    if (!allocationWindowVisible) return
    closeAllPopovers()
  }, [allocationWindowVisible])

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
        hoverTimeoutRef.current = null
      }
    }
  }, [])

  const allocatedCount = teamMembers?.length || 0
  const missingAllocationsNumber = Math.max(numberSupply - allocatedCount, 0)

  // check if we should show tm icon for 1 assigned team member
  const shouldShowSingleTeamMemberIcon = () => {
    // same behavior for team member view
    if (type === 'user') return false

    // show icon when there's one tm on shift & resi
    return (type === RESIDENT || type === SHIFT) && allocatedCount === 1
  }

  let residentName = ''

  if (resident?.name?.first && resident?.name?.last) {
    residentName = `${resident.name.first} ${resident.name.last}`
  } else if (deletedServiceUser) {
    residentName = t('DELETED_NOMINATIVE', { nominative: residentNominative })
  } else {
    residentName = title
  }

  const isDraft = status === RotaInstanceStatus.DRAFT
  const showWarnings = !isDraft && warnings.length > 0
  const isModified = modified && !isDraft
  const expectedDurationInMinutes = convertToMinutes(expectedDuration)

  return (
    <>
      <EventTitlePopover
        original={args.original}
        hoursText={expectedDuration}
        timeInterval={timeInterval}
        isOpen={titlePopoverOpen}
        setIsOpen={setTitlePopoverOpen}
        disabled={
          allocationPopoverOpen ||
          quickAllocationPopoverOpen ||
          warningPopoverOpen
        }
      >
        <Container
          $color={color}
          $showWarnings={showWarnings}
          onClick={handleOpenDrawer}
          onMouseEnter={handleEventMouseEnter}
          onMouseLeave={handleEventMouseLeave}
          data-testid={baseId}
        >
          {isModified && <Blob />}
          <LeftContainer>
            <Row>
              <IconTitleContainer>
                {showWarnings ? (
                  <WarningPopover
                    warningsData={warnings}
                    isOpen={warningPopoverOpen}
                    setIsOpen={(open: boolean) => {
                      setWarningPopoverOpen(open)
                      if (open) {
                        setTitlePopoverOpen(false)
                      } else {
                        handleInnerPopoverClose()
                      }
                    }}
                  >
                    <WarningIconWrapper>
                      <WarningIcon />
                    </WarningIconWrapper>
                  </WarningPopover>
                ) : (
                  status !== RotaInstanceStatus.PENDING && (
                    <EventIcon status={status} />
                  )
                )}
                <Title
                  level={1.5}
                  weight="bold"
                  $strikethrough={status === RotaInstanceStatus.CANCELLED}
                  data-testid={commonPatterns.field(baseId, 'title')}
                >
                  {type === RESIDENT ? title : residentName}
                </Title>
              </IconTitleContainer>
            </Row>
            <Details>
              <HoursSection>
                <DetailText
                  level={2}
                  data-testid={commonPatterns.field(baseId, 'time-interval')}
                >
                  {timeInterval}
                </DetailText>
                {isOvernight && (
                  <OvernightText level={2}>{t('PLUS_ONE')}</OvernightText>
                )}
              </HoursSection>
              <Separator level={2}>·</Separator>
              {flexiType && flagsRosteringParkedFeaturesEnabled && (
                <>
                  <Separator level={2}>·</Separator>
                  <DetailText level={2}>{t('FLEXI')}</DetailText>
                </>
              )}
            </Details>
          </LeftContainer>
          {(!isDailyView || expectedDurationInMinutes > 30) && (
            <RightContainer>
              {/* <TertiaryText level={2}>{instanceType}</TertiaryText> */}
              <PopoversContainer>
                {publishedRota ? (
                  <AllocationsContainer>
                    <AllocationActions>
                      {allocatedCount > 0 && (
                        <StyledAvatar
                          size={24}
                          url={
                            teamMembers[0]?.carer?.picture as unknown as string
                          }
                        />
                      )}
                      {allocatedCount > 1 && (
                        <PlusText level={2}>+{allocatedCount - 1}</PlusText>
                      )}
                    </AllocationActions>
                  </AllocationsContainer>
                ) : (
                  <>
                    {quickAllocationPopoverOpen && (
                      <QuickAllocationPopover
                        isOpen={quickAllocationPopoverOpen}
                        setIsOpen={(open: boolean) => {
                          setQuickAllocationPopoverOpen(open)
                          if (open) {
                            setTitlePopoverOpen(false)
                          } else {
                            handleInnerPopoverClose()
                          }
                        }}
                        setAllocationWindowVisible={setAllocationWindowVisible}
                        revisionId={args.original.id}
                      >
                        <AllocationPopover
                          missingAllocationsNumber={missingAllocationsNumber}
                          hasAllocatedTeamMembers={allocatedCount > 0}
                          original={args.original}
                          args={args}
                          setAllocationWindowVisible={
                            deletedServiceUser
                              ? () => {}
                              : setAllocationWindowVisible
                          }
                          setDrawerVisible={setDrawerAllocatedTeamVisible}
                          isOpen={allocationPopoverOpen}
                          setIsOpen={(open: boolean) => {
                            setAllocationPopoverOpen(open)
                            if (open) {
                              setTitlePopoverOpen(false)
                            } else {
                              handleInnerPopoverClose()
                            }
                          }}
                          disabled={quickAllocationPopoverOpen}
                        >
                          <AllocationsContainer onClick={handleIconClick}>
                            <AllocationActions>
                              {numberSupply === 1 &&
                                (allocatedCount > 1 ||
                                  shouldShowSingleTeamMemberIcon()) && (
                                  <StyledAvatar
                                    size={24}
                                    url={
                                      teamMembers[0]?.carer
                                        ?.picture as unknown as string
                                    }
                                  />
                                )}
                              {numberSupply === 1 && allocatedCount < 1 && (
                                <AllocateIconContainer
                                  data-testid={commonPatterns.button(
                                    baseId,
                                    'allocate'
                                  )}
                                >
                                  <AllocateIcon />
                                </AllocateIconContainer>
                              )}
                              {numberSupply > 1 && (
                                <>
                                  {missingAllocationsNumber > 0 ? (
                                    <AllocateIconContainer
                                      data-testid={commonPatterns.button(
                                        baseId,
                                        'allocate'
                                      )}
                                    >
                                      <AllocateIcon />
                                    </AllocateIconContainer>
                                  ) : (
                                    <StyledAvatar
                                      size={24}
                                      url={
                                        teamMembers[0]?.carer
                                          ?.picture as unknown as string
                                      }
                                    />
                                  )}
                                  <PlusText level={2}>
                                    +{numberSupply - 1}
                                  </PlusText>
                                </>
                              )}
                            </AllocationActions>
                          </AllocationsContainer>
                        </AllocationPopover>
                      </QuickAllocationPopover>
                    )}
                    {!quickAllocationPopoverOpen && (
                      <AllocationPopover
                        missingAllocationsNumber={missingAllocationsNumber}
                        hasAllocatedTeamMembers={allocatedCount > 0}
                        original={args.original}
                        args={args}
                        setAllocationWindowVisible={
                          deletedServiceUser
                            ? () => {}
                            : setAllocationWindowVisible
                        }
                        setDrawerVisible={setDrawerAllocatedTeamVisible}
                        isOpen={allocationPopoverOpen}
                        setIsOpen={(open: boolean) => {
                          setAllocationPopoverOpen(open)
                          if (open) {
                            setTitlePopoverOpen(false)
                          } else {
                            handleInnerPopoverClose()
                          }
                        }}
                        disabled={quickAllocationPopoverOpen}
                      >
                        <AllocationsContainer onClick={handleIconClick}>
                          <AllocationActions>
                            {numberSupply === 1 &&
                              (allocatedCount > 1 ||
                                shouldShowSingleTeamMemberIcon()) && (
                                <StyledAvatar
                                  size={24}
                                  url={
                                    teamMembers[0]?.carer
                                      ?.picture as unknown as string
                                  }
                                />
                              )}
                            {numberSupply === 1 && allocatedCount < 1 && (
                              <AllocateIconContainer
                                data-testid={commonPatterns.button(
                                  baseId,
                                  'allocate'
                                )}
                              >
                                <AllocateIcon />
                              </AllocateIconContainer>
                            )}
                            {numberSupply > 1 && (
                              <>
                                {missingAllocationsNumber > 0 ? (
                                  <AllocateIconContainer
                                    data-testid={commonPatterns.button(
                                      baseId,
                                      'allocate'
                                    )}
                                  >
                                    <AllocateIcon />
                                  </AllocateIconContainer>
                                ) : (
                                  <StyledAvatar
                                    size={24}
                                    url={
                                      teamMembers[0]?.carer
                                        ?.picture as unknown as string
                                    }
                                  />
                                )}
                                <PlusText level={2}>
                                  +{numberSupply - 1}
                                </PlusText>
                              </>
                            )}
                          </AllocationActions>
                        </AllocationsContainer>
                      </AllocationPopover>
                    )}
                  </>
                )}
              </PopoversContainer>
            </RightContainer>
          )}
        </Container>
      </EventTitlePopover>
      {allocationWindowVisible && (
        <AllocationWindow
          allocationWindowVisible={allocationWindowVisible}
          onCancel={() => setAllocationWindowVisible(false)}
          revisionDetail={args.original}
        />
      )}
    </>
  )
}

const StyledAvatar = styled(Avatar)`
  border: 0.5px solid ${(props) => props.theme.color.neutral.color7};
`

const PopoversContainer = styled.div`
  display: flex;
  align-items: center;
`

const AllocateIconContainer = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${(props) => props.theme.color.neutral.color7};
  border-radius: 50%;
  width: 24px;
  height: 24px;
  justify-content: center;
  cursor: pointer;
`

const LeftContainer = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
`

const RightContainer = styled.div``

const WarningIconWrapper = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`

const PlusText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color4};
  margin-left: 2px;
`

const AllocationsContainer = styled.div`
  display: flex;
  align-items: center;
  height: 16px;
  position: relative;

  &:hover {
    ${StyledAvatar},${AllocateIconContainer}, &:hover ${PlusText} {
      border-color: ${({ theme }) => theme.color.hover.primary};
      color: ${({ theme }) => theme.color.hover.primary};
    }
  }
`

const Container = styled.div<{ $color: string; $showWarnings: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  width: 100%;
  background: ${(props) => (props.$showWarnings ? '#FFF7F0' : 'white')};
  padding: 4px 4px 4px 4px;
  border: 1px solid ${(props) => props.theme.color.neutral.color7};
  border-radius: 4px;
  border-left: 5px solid ${(props) => props.$color};
  position: relative;

  &:hover {
    border: 1px solid ${(props) => props.theme.color.neutral.color6};
    border-left: 5px solid ${(props) => props.$color};
    box-shadow: 0px 0px 8px 0px rgba(34, 34, 34, 0.2);
  }
`

const Title = styled(Text)<{ $strikethrough: boolean }>`
  color: ${(props) => props.theme.color.neutral.color3};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
  text-decoration: ${({ $strikethrough }) =>
    $strikethrough ? 'line-through' : undefined};
`

const OvernightText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
  position: relative;
  top: 0;
  transform: translateY(-10%);
`

const HoursSection = styled.div`
  display: flex;
  gap: 1px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
`

const DetailText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color3};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
`

const Blob = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  height: 10px;
  width: 10px;
  border-radius: 0 3px 0 4px;
  background: ${(props) => props.theme.color.status.popout};
  border-left: 0.5px solid ${(props) => props.theme.color.neutral.color9};
  border-bottom: 0.5px solid ${(props) => props.theme.color.neutral.color9};
`

const Details = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`

const IconTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`

const AllocationActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Separator = styled(DetailText)`
  color: ${(props) => props.theme.color.neutral.color5};
`

export default Event
